import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardSidebar from '../../components/navigation/DashboardSidebar';
import DashboardHeader from '../../components/navigation/DashboardHeader';
import DashboardFooter from '../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../components/navigation/MobileMenuButton';

import Icon from '../../components/AppIcon';
import NuovaRichiestaAdminModal from '../../components/NuovaRichiestaAdminModal';

import RichiesteFerie from 'components/navigation/RichiesteFerieDipendenti';
import { supabase } from '../../lib/supabase';

const FerieAdminPage = () => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNuovaRichiestaOpen, setIsNuovaRichiestaOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          setLoadError(t('ferie.admin.errors.no_session', { defaultValue: 'Sessione non valida. Effettua nuovamente il login.' }));
          return;
        }

        const { data: profile, error } = await supabase
          .from('profili')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setUserData(profile);
      } catch (err) {
        console.error("Error fetching profile:", err.message);
        setLoadError(t('ferie.admin.errors.profile_load_failed', { defaultValue: 'Impossibile caricare il profilo.' }));
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [t]);

  const handleRichiestaSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // ── Stanja renderovanja glavnog sadržaja ────────────────────────────────────
  // Prije je uslov bio SAMO `!loading && userData?.id_azienda ? (...) : (spinner)`,
  // što znači da bi korisnik BEZ id_azienda vidio VJEČITI spinner nakon što se
  // loading završi, bez ikakve poruke o stvarnom problemu. Sad su tri odvojena
  // stanja: (1) učitavanje, (2) greška/nema kompanije, (3) uspješno učitano.
  const renderMainContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-10">
          <Icon name="Loader2" size={24} className="animate-spin text-primary/50" />
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-2xl text-center">
          <Icon name="AlertTriangle" size={28} className="mx-auto mb-3 text-destructive" />
          <p className="text-sm font-bold text-destructive">{loadError}</p>
        </div>
      );
    }

    if (!userData?.id_azienda) {
      return (
        <div className="p-6 bg-orange-500/10 border border-orange-500/20 rounded-2xl text-center">
          <Icon name="AlertTriangle" size={28} className="mx-auto mb-3 text-orange-500" />
          <p className="text-sm font-bold text-orange-600">
            {t('ferie.admin.errors.no_company_assigned', { defaultValue: 'Non hai un\'azienda assegnata al tuo profilo. Contatta il supporto.' })}
          </p>
        </div>
      );
    }

    return (
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
        <RichiesteFerie idAzienda={userData.id_azienda} refreshTrigger={refreshTrigger} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

      <DashboardHeader userName={t('profile_page.title')} />

      <main className="main-content with-sidebar py-10 px-4 mt-20">
        <div className="max-w-5xl mx-auto">

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-bold uppercase text-[10px] tracking-widest"
            >
              <div className="p-2 bg-muted rounded-xl group-hover:bg-primary/10 transition-colors">
                <Icon name="ArrowLeft" size={16} />
              </div>
              {t('profile_page.back')}
            </button>

            {!loading && userData?.id_azienda && (
              <button
                onClick={() => setIsNuovaRichiestaOpen(true)}
                className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-2xl hover:opacity-90 transition-all group shadow-lg shadow-primary/20 active:scale-95"
              >
                <div className="group-hover:scale-110 transition-transform">
                  <Icon name="Plus" size={20} />
                </div>
                <span className="font-black uppercase text-[10px] tracking-widest">
                  {t('ferie.new_request_btn')}
                </span>
              </button>
            )}
          </div>

          <div className="mb-10">
            <h1 className="text-2xl font-black uppercase tracking-tighter text-foreground">
              {t('ferie.admin.page_title')}
            </h1>
            <div className="h-1 w-20 bg-primary mt-2 rounded-full"></div>
          </div>

          {renderMainContent()}

        </div>
      </main>

      {userData?.id_azienda && (
        <NuovaRichiestaAdminModal
          isOpen={isNuovaRichiestaOpen}
          onClose={() => setIsNuovaRichiestaOpen(false)}
          idAzienda={userData.id_azienda}
          adminId={userData.id}
          onSuccess={handleRichiestaSuccess}
        />
      )}

      <DashboardFooter />
    </div>
  );
};

export default FerieAdminPage;